import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Building2, Users, GraduationCap, Briefcase, BookOpen, Layers, MapPin, Phone, Mail,
  Calendar, Globe, Crown, Pencil, Trash2, RotateCcw, Image as ImgIcon, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import AdminSetPlanDialog from "@/components/AdminSetPlanDialog";

const STATUS_PILL = {
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  pending:  "bg-amber-50 text-amber-700 border-amber-200",
  rejected: "bg-rose-50 text-rose-700 border-rose-200",
};

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className={`rounded-2xl p-3 border ${accent || "bg-slate-50 border-slate-100"}`}>
      <div className="flex items-center gap-2 text-xs uppercase font-semibold text-slate-500 tracking-wider">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="text-2xl font-heading font-bold mt-1">{value ?? "—"}</div>
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2 text-sm">
      <Icon className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
      <div className="min-w-0 flex-1"><span className="text-slate-500">{label}: </span><span className="text-slate-800 break-words">{value}</span></div>
    </div>
  );
}

/**
 * Pop-up detail for a school OR a branch. Modern card layout with hero band,
 * stats grid, info rows, and contextual action buttons (Edit / Deactivate /
 * Restore for super admin).
 *
 * Props:
 *   open, onClose, kind ('school'|'branch'), id, role, onEdit, onChanged
 */
export default function EntityDetailDialog({ open, onClose, kind, id, role, onEdit, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [setPlanOpen, setSetPlanOpen] = useState(false);

  useEffect(() => {
    if (!open || !id) return;
    setLoading(true); setData(null);
    (async () => {
      try {
        const r = await api.get(`/${kind === "school" ? "schools" : "branches"}/${id}/detail`);
        setData(r.data);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [open, id, kind]);

  const isSuper = role === "super_admin" || role === "sub_super_admin";
  const isOwner = role === "school_owner" || role === "sub_owner";
  const entity = data ? (kind === "school" ? data.school : data.branch) : null;
  const stats = data?.stats || {};
  const isInactive = entity?.is_active === false;

  const deactivate = async () => {
    if (!window.confirm(`Deactivate this ${kind}? It will be hidden everywhere but recoverable by Super Admin.`)) return;
    setBusy(true);
    try {
      const path = kind === "school" ? `/schools/${id}/deactivate` : `/branches/${id}/deactivate`;
      await api.post(path);
      toast.success(`${kind === "school" ? "School" : "Branch"} deactivated`);
      onChanged?.(); onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const restore = async () => {
    setBusy(true);
    try {
      const path = kind === "school" ? `/admin/schools/${id}/restore` : `/admin/branches/${id}/restore`;
      await api.post(path);
      toast.success(`${kind === "school" ? "School" : "Branch"} restored`);
      onChanged?.(); onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto p-0 rounded-3xl border-0 shadow-2xl" data-testid="entity-detail-dialog">
        {loading ? (
          <div className="p-10 text-center text-slate-500">Loading…</div>
        ) : !data ? (
          <div className="p-10 text-center text-slate-500">Not found.</div>
        ) : (
          <>
            {/* Hero band — solid color or photo */}
            <div className="relative h-32 bg-gradient-to-br from-[#1E40AF] via-[#2563EB] to-[#3B82F6] rounded-t-3xl overflow-hidden">
              {(entity?.hero_photo_base64 || entity?.cover_image_base64) && (
                <img src={entity.hero_photo_base64 || entity.cover_image_base64} alt="" className="absolute inset-0 w-full h-full object-cover opacity-80" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute -bottom-8 left-6 flex items-end gap-3">
                <div className="w-20 h-20 rounded-3xl bg-white shadow-floating flex items-center justify-center border-4 border-white overflow-hidden">
                  {entity?.logo_base64 ? (
                    <img src={entity.logo_base64} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <Building2 className="w-9 h-9 text-[#2563EB]" />
                  )}
                </div>
              </div>
              <div className="absolute top-3 right-3 flex items-center gap-1.5">
                {entity?.status && (
                  <span className={`text-[10px] uppercase font-bold tracking-wider rounded-full px-2 py-0.5 border ${STATUS_PILL[entity.status] || "bg-white/20 text-white"}`}>
                    {entity.status}
                  </span>
                )}
                {isInactive && (
                  <span className="text-[10px] uppercase font-bold tracking-wider rounded-full px-2 py-0.5 bg-rose-100 text-rose-700 border border-rose-200">
                    Deactivated
                  </span>
                )}
              </div>
            </div>

            <div className="pt-12 px-6 pb-6 space-y-5">
              <DialogHeader className="text-left space-y-0.5">
                <DialogTitle className="font-heading text-2xl font-bold pr-12">{entity?.name}</DialogTitle>
                <DialogDescription className="text-sm">
                  {kind === "school"
                    ? (entity?.public_description_md || entity?.tagline || "Approved school")
                    : (data.school?.name ? `Branch of ${data.school.name}` : "Branch")}
                </DialogDescription>
              </DialogHeader>

              {/* Stats grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                {kind === "school" ? (
                  <>
                    <Stat icon={Layers}     label="Branches"  value={stats.branches} accent="bg-blue-50 border-blue-100" />
                    <Stat icon={GraduationCap} label="Students" value={stats.students} accent="bg-emerald-50 border-emerald-100" />
                    <Stat icon={Briefcase}  label="Employees" value={stats.employees} accent="bg-amber-50 border-amber-100" />
                    <Stat icon={Crown}      label="Plan"      value={data.plan?.name || "Free"} accent="bg-violet-50 border-violet-100" />
                  </>
                ) : (
                  <>
                    <Stat icon={GraduationCap} label="Students"  value={stats.students} accent="bg-emerald-50 border-emerald-100" />
                    <Stat icon={Briefcase}  label="Employees" value={stats.employees} accent="bg-amber-50 border-amber-100" />
                    <Stat icon={BookOpen}   label="Classes"   value={stats.classes}   accent="bg-blue-50 border-blue-100" />
                    <Stat icon={Layers}     label="Sections"  value={stats.sections}  accent="bg-violet-50 border-violet-100" />
                  </>
                )}
              </div>

              {/* Info section */}
              <div className="space-y-1.5">
                <Row icon={MapPin} label="Address"     value={entity?.address || entity?.location} />
                <Row icon={Phone}  label="Phone"       value={entity?.phone || entity?.contact_phone} />
                <Row icon={Mail}   label="Email"       value={entity?.email || entity?.contact_email} />
                <Row icon={Globe}  label="Website"     value={entity?.website} />
                <Row icon={Sparkles} label="Slug"      value={entity?.slug && `/school/${entity.slug}`} />
                <Row icon={Calendar} label="Created"   value={entity?.created_at && new Date(entity.created_at).toLocaleDateString()} />
                <Row icon={ImgIcon}  label="Description" value={kind === "branch" ? entity?.description : null} />
              </div>

              <DialogFooter className="gap-2 sm:gap-2 flex-wrap pt-2 border-t">
                <Button variant="outline" onClick={onClose} data-testid="entity-detail-close">Close</Button>
                {isSuper && kind === "school" && !isInactive && (
                  <Button onClick={() => setSetPlanOpen(true)} variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="entity-detail-set-plan">
                    <Crown className="w-4 h-4 mr-1.5" /> Set Plan
                  </Button>
                )}
                {(isOwner || isSuper) && !isInactive && (
                  <Button onClick={() => { onEdit?.(entity); }} className="rounded-full bg-[#2563EB] hover:bg-[#1D4ED8]" data-testid="entity-detail-edit">
                    <Pencil className="w-4 h-4 mr-1.5" /> Edit
                  </Button>
                )}
                {isOwner && !isInactive && (
                  <Button onClick={deactivate} disabled={busy} variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="entity-detail-deactivate">
                    <Trash2 className="w-4 h-4 mr-1.5" /> Deactivate
                  </Button>
                )}
                {isSuper && isInactive && (
                  <Button onClick={restore} disabled={busy} className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="entity-detail-restore">
                    <RotateCcw className="w-4 h-4 mr-1.5" /> Restore
                  </Button>
                )}
              </DialogFooter>
            </div>
          </>
        )}
      </DialogContent>
      {/* SA-only inline plan override */}
      {kind === "school" && entity && (
        <AdminSetPlanDialog
          open={setPlanOpen}
          onClose={() => setSetPlanOpen(false)}
          school={entity}
          onChanged={() => { setSetPlanOpen(false); onChanged?.(); }}
        />
      )}
    </Dialog>
  );
}
